import React from 'react';

import { IValidatingCarrier } from 'features/transactions/types';
import { CountryCodeEnum, INetsuiteSyncStatusDTO } from '../common/types';
import { ICustomerCorpoDTO, ICustomerLeisureDTO } from 'features/customers/types';

import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import { AccomodationProviderItem } from '../common/components/AutocompleteAccomodationProviders';
import { AttachMoney } from '@mui/icons-material';
import LuggageIcon from '@mui/icons-material/Luggage';
import ExtensionIcon from '@mui/icons-material/Extension';

export interface ISalesOrderList {
	page: number;
	size: number;
	total: number;
	sort?: string;
	elements: ISaleOrderListItem[];
}

export enum PaymentReceivedRelationTypeEnum {
	MVOrderTransaction = 'MVOrderTransaction',
	VNOrderTransaction = 'VNOrderTransaction',
	CashPayment = 'CashPayment',
	WireTransferPayment = 'WireTransferPayment',
	MVHotelReserve = 'MVHotelReserve',
	VNFlightReserve = 'VNFlightReserve',
	VNHotelReserveMV = 'VNHotelReserveMV',
}

export enum PaymentReceivedPlatformTypeEnum {
	PaymentSimulator = 'PaymentSimulator',
	SiemprePago = 'SiemprePago',
	Bancard = 'Bancard',
	ItauPoints = 'ItauPoints',
	ATCRedEnlace = 'ATCRedEnlace',
	Wompi = 'Wompi',
	Cash = 'Cash',
	WireTransfer = 'WireTransfer',
}

export interface ITransaction {
	orderId?: string;
	transactionId: string;
	authorizationId?: number;
	transactionAmount: number;
	transactionCurrency: string;
	amountInMainCurrency: number;
	relationType: PaymentReceivedRelationTypeEnum; // Puedes ajustar el tipo según sea necesario
	platformType: PaymentReceivedPlatformTypeEnum; // Puedes ajustar el tipo según sea necesario
	createdAt: string;
	observations?: string;
}

export interface ISaleOrderDetail {
	id: string;
	segmentType: SegmentEnum;
	dealId: number;
	isBitrixDeal: boolean;
	quotationId: number;
	clientReference?: string;
	automaticSyncWithNetsuite: boolean;
	created: string;
	createdBy: string;
	createdById: number;
	updated: string;
	updatedBy: string;
	currency: string;
	travelDepartureDate: string;
	travelReturnDate: string;
	saleAmount: number;
	totalCost: number;
	grossProfit: number;
	grossProfitPercentage: number;
	chargedAmount: number;
	pendingPayAmount: number;
	destinations: string;
	orderNumber: number;
	channel: IChannel;
	//TODO : en el proximo PR cuando se implemente en la Sale Order se agregan
	passengers: IPassenger[];
	services: IServices[];
	customer: ICustomerLeisureDTO | ICustomerCorpoDTO;
	syncStatus: INetsuiteSyncStatusDTO;
}

export interface IPatchSaleOrder {
	channelId?: number;
	segmentType?: string;
	clientReference?: string;
	automaticSyncWithNetsuite?: boolean;
}

// Nuevas interfaces para el calendario
export interface IUserSummary {
	id: number;
	username: string;
	email: string;
	name: string;
	active: boolean;
	team?: string;
}

export interface ICalendarSaleOrder {
	saleOrderId: string;
	segmentType: string;
	travelDepartureDate: string;
	createdBy: string;
	dealId: number;
	isBitrixDeal: boolean;
	pendingPayAmount: number;
	mainPassengerCompleteName: string;
	customersCompleteName: string[];
}

export interface ICalendarDayData {
	travelDepartureDate: string;
	saleOrders: ICalendarSaleOrder[];
}

/** Estado de facturación a clientes calculado por la API (facturas propias, no Netsuite). */
export enum SaleOrderInvoicingStatusEnum {
	Pending = 'Pending',
	Partial = 'Partial',
	Total = 'Total',
}

export const saleOrderInvoicingStatusTypes: { id: SaleOrderInvoicingStatusEnum; name: string }[] = [
	{ id: SaleOrderInvoicingStatusEnum.Pending, name: '🔴 Pendiente' },
	{ id: SaleOrderInvoicingStatusEnum.Partial, name: '🟡 Parcial' },
	{ id: SaleOrderInvoicingStatusEnum.Total, name: '🟢 Total' },
];

export const getSaleOrderInvoicingStatusName = (status?: SaleOrderInvoicingStatusEnum): string =>
	saleOrderInvoicingStatusTypes.find((s) => s.id === status)?.name ?? '-';

export interface ISaleOrderListItem {
	id: string;
	dealId: number;
	isBitrixDeal: boolean;
	orderNumber: number;
	quotationId?: string;
	clientReference?: string;
	created: string;
	currency: string;
	travelDepartureDate?: string;
	travelReturnDate?: string;
	saleAmount?: number;
	totalCost?: number;
	grossProfit?: number;
	chargedAmount?: number;
	pendingPayAmount?: number;
	destinations?: string;
	servicesTypes?: TypeServiceEnum[];
	mainPassengerCompleteName?: string;
	customersCompleteName?: string[];
	market: CountryCodeEnum;
	updated?: string;
	createdBy: string;
	automaticSyncWithNetsuite?: boolean;
	lastSuccessSyncAt?: string;
	segmentType: SegmentEnum;
	channel?: { id: number; name: string };
	matchSearchValueWithServiceId?: boolean;
	/** Monto facturado a clientes (facturas Cash/Credit en Draft/Issued). Siempre presente. */
	invoicedAmount: number;
	/** saleAmount − invoicedAmount, clampeado a 0. Null si la orden no tiene saleAmount. */
	pendingInvoicingAmount?: number;
	/** Null si la orden no tiene saleAmount. */
	invoicingStatus?: SaleOrderInvoicingStatusEnum;
	/** true cuando travelReturnDate <= hoy. */
	isReturnDateReached: boolean;
}

export interface ISalesOrderCustomer {
	page: number;
	size: number;
	total: number;
	sort?: string;
	elements: ISaleOrderCustomerListItem[];
}

export interface ISaleOrderCustomerListItem {
	id: string;
	name: string;
	lastName: string;
	email: string;
	phone: string;
	taxIdentificationNumber: string;
	street: string;
	cityName: string;
	stateName: string;
	postaleCode: string;
	country: ICodeName;
}

export interface IPassengerForm {
	id: string;
	keyId: string;
	name: string;
	lastName: string;
	state?: string;
	province: string;
	postalCode?: string;
	address?: string;
	municipality: string;
	countryId: string;
	taxPayerTypeId?: string;
	taxRegimeId?: string;
	taxResidenceTypeId?: string;
	fiscalResponsibilityId?: string;
	isPhysicalPerson: boolean;
	invoiceable: boolean;
	birthdate: string;
	mainPassenger: boolean;
	documents: {
		keyId: string;
		documentType: PassengerDocumentEnum;
		country: string;
		documentNumber: string;
		expirationDate: string;
		image?: string | null;
	}[];
	ageAtEndTravel?: number;
	phone?: string;
	email?: string;
	documentType?: number;
	documentNumber?: string;
	frequentFlyerNumbers: IFrequentFlyerNumber[];
	documentVerificationNumber?: string;
	syncStatus: INetsuiteSyncStatusDTO | null;
	seatPreference?: string;
}

export interface IDocumentForm {
	keyId: string;
	documentType: PassengerDocumentEnum;
	country: string;
	documentNumber: string;
	expirationDate: string;
	image?: string | null;
}

export interface IFrequentFlyerNumber {
	airlineCode: string;
	frequentFlyerNumber: string;
}

export interface IPassenger {
	id: string;
	keyId: string;
	name: string;
	lastName: string;
	state?: string;
	province?: string;
	municipality?: string;
	countryId: number;
	birthdate?: string;
	mainPassenger: boolean;
	documents: IDocuments[];
	frequentFlyerNumbers: IFrequentFlyerNumber[];
	ageAtEndTravel?: number;
	taxPayerTypeId?: number;
	taxRegimeId?: number;
	taxResidenceTypeId?: number;
	fiscalResponsibilityId?: number;
	isPhysicalPerson?: boolean;
	invoiceable?: boolean;
	phone?: string;
	email?: string;
	documentType?: number;
	documentNumber?: string;
	documentVerificationNumber?: string;
	syncStatus?: INetsuiteSyncStatusDTO;
	seatPreference?: string;
}

export interface ICodeName {
	code: string;
	name: string;
}

export interface IDocuments {
	keyId: string;
	documentType: number;
	country: ICodeName;
	documentNumber: string;
	expirationDate: string;
	image?: string | null;
}

export enum PassengerDocumentEnum {
	CEDULA = 'Cedula',
	DRIVERLICENSE = 'DriverLicense',
	PASSPORT = 'Passport',
}

export interface IPassengerDocumentType {
	id: PassengerDocumentEnum;
	name: string;
}

export const passengerDocumentType: IPassengerDocumentType[] = [
	{
		id: PassengerDocumentEnum.CEDULA,
		name: 'Cedula',
	},
	{
		id: PassengerDocumentEnum.DRIVERLICENSE,
		name: 'DriverLicense',
	},
	{
		id: PassengerDocumentEnum.PASSPORT,
		name: 'Passport',
	},
];

export interface IPassengerAutocomplete {
	passengerId: string;
	name: string;
	email: string;
	lastName: string;
	birthdate: string;
	documents: IDocumentsAutocomplete[];
	isPhysicalPerson: boolean;
}

export interface IDocumentsAutocomplete {
	documentType: PassengerDocumentEnum;
	documentNumber: string;
}

export interface IRelatedPax {
	id: string;
	name: string;
	lastName: string;
	birthdate: string;
	invoiceable: boolean;
	isPhysicalPerson: boolean;
	documentType: number;
	documentTypeName: string;
	documentNumber: string;
	documentVerificationNumber: string;
	completeDocumentNumber: string;
}

export interface ISaleOrderCreateForm {
	dealId: number;
	isBitrixDeal: boolean;
	quotationId?: number;
	segmentType: SegmentEnum;
	channelId: number;
	clientReference?: string;
}

export interface ICurrency {
	code: string;
	name: string;
}

export interface ICurrencyByMarket {
	market: CountryCodeEnum;
	currencies: ICurrency[];
}

export enum SegmentEnum {
	LEISURE = 'Leisure',
	CORPO = 'Corpo',
	WHOLESALER = 'Wholesaler',
	COLLABORATORS = 'Collaborators',
}

export interface ISegmentType {
	id: SegmentEnum;
	name: string;
}

export const segmentTypes: ISegmentType[] = [
	{
		id: SegmentEnum.LEISURE,
		name: 'Leisure',
	},
	{
		id: SegmentEnum.CORPO,
		name: 'Corpo',
	},
	{
		id: SegmentEnum.WHOLESALER,
		name: 'Mayorista',
	},
	{
		id: SegmentEnum.COLLABORATORS,
		name: 'Colaboradores',
	},
];

const segmentTypeNameMap: Record<SegmentEnum, string> = segmentTypes.reduce(
	(acc, curr) => {
		acc[curr.id] = curr.name;
		return acc;
	},
	{} as Record<SegmentEnum, string>,
);

export const getNameSegmentType = (segmentType: SegmentEnum): string => {
	return segmentTypeNameMap[segmentType] ?? '';
};

// Segmentos a los que sales-order-api les manda el WhatsApp por saldo pendiente.
// Tiene que coincidir con SegmentType.segmentTypesAllowSendPendingPayMessage del backend.
export const segmentTypesAllowPendingPayMessage: SegmentEnum[] = [SegmentEnum.LEISURE, SegmentEnum.COLLABORATORS];

export const getNameServiceType = (servicesType: TypeServiceEnum): string => {
	const nameServiceType = serviceTypes.find((x) => x.id == servicesType)?.name;
	return nameServiceType ? nameServiceType : '';
};

export enum TypeNationalitesEnum {
	Andorra,
	UnitedArabEmirates,
	Afghanistan,
	AntiguaandBarbuda,
	Anguilla,
	Albania,
	Armenia,
	NetherlandsAntilles,
	Angola,
	Antarctica,
	Argentina,
	AmericanSamoa,
	Austria,
	Australia,
	Aruba,
	ÅlandIslands,
	Azerbaijan,
	BosniaandHerzegovina,
	Barbados,
	Bangladesh,
	Belgium,
	BurkinaFaso,
	Bulgaria,
	Bahrain,
	Burundi,
	Benin,
	SaintBarthélemy,
	Bermuda,
	BruneiDarussalam,
	Bolivia,
	SintEustatiusandSaba,
	Brazil,
	Bahamas,
	Bhutan,
	BouvetIsland,
	Botswana,
	Belarus,
	Belize,
	Canada,
	CocosKeelingIslands,
	CongoRepublic,
	African,
	Congo,
	Switzerland,
	CôteDIvoire,
	CookIslands,
	Chile,
	Cameroon,
	China,
	Colombia,
	CostaRica,
	Cuba,
	CapeVerde,
	Curaçao,
	ChristmasIsland,
	Cyprus,
	CzechRepublic,
	Germany,
	Djibouti,
	Denmark,
	Dominica,
	DominicanRepublic,
	Algeria,
	Ecuador,
	Estonia,
	Egypt,
	WesternSahara,
	Eritrea,
	España,
	Ethiopia,
	Finland,
	Fiji,
	FalklandIslandsMalvinas,
	Micronesia,
	FaroeIslands,
	France,
	Gabon,
	UnitedKingdom,
	Grenada,
	Georgia,
	FrenchGuiana,
	Guernsey,
	Ghana,
	Gibraltar,
	Greenland,
	Gambia,
	Guinea,
	Guadeloupe,
	EquatorialGuinea,
	Greece,
	SouthGeorgia,
	Guatemala,
	Guam,
	GuineaBissau,
	Guyana,
	HongKong,
	Honduras,
	Croatia,
	Haiti,
	Hungary,
	Indonesia,
	Ireland,
	Israel,
	IsleofMan,
	India,
	BritishIndian,
	Iraq,
	IranIslamic,
	Iceland,
	Italy,
	Jersey,
	Jamaica,
	Jordan,
	Japan,
	Kenya,
	Kyrgyzstan,
	Cambodia,
	Kiribati,
	Comoros,
	SaintKittsAndNevis,
	KoreaDemocratic,
	KoreaRepublic,
	Kuwait,
	CaymanIslands,
	Kazakhstan,
	LaoPeoples,
	Lebanon,
	SaintLucia,
	Liechtenstein,
	SriLanka,
	Liberia,
	Lesotho,
	Lithuania,
	Luxembourg,
	Latvia,
	Libya,
	Morocco,
	Monaco,
	MoldovaRepublic,
	Montenegro,
	SaintMartin,
	Madagascar,
	MarshallIslands,
	Macedonia,
	Mali,
	Myanmar,
	Mongolia,
	Macao,
	NorthernMarianaIslands,
	Martinique,
	Mauritania,
	Montserrat,
	Malta,
	Mauritius,
	Maldives,
	Malawi,
	Mexico,
	Malaysia,
	Mozambique,
	Namibia,
	NewCaledonia,
	Niger,
	NorfolkIsland,
	Nigeria,
	Nicaragua,
	Netherlands,
	Norway,
	Nepal,
	Nauru,
	Niue,
	NewZealand,
	Oman,
	Panama,
	Peru,
	FrenchPolynesia,
	PapuaNewGuinea,
	Philippines,
	Pakistan,
	Poland,
	Pitcairn,
	PuertoRico,
	Palestine,
	Portugal,
	Palau,
	Paraguay,
	Qatar,
	Réunion,
	Romania,
	Serbia,
	RussianFederation,
	Rwanda,
	SaudiArabia,
	SolomonIslands,
	Seychelles,
	Sudan,
	Sweden,
	Singapore,
	SaintHelena,
	Slovenia,
	Slovakia,
	SierraLeone,
	SanMarino,
	Senegal,
	Somalia,
	Suriname,
	SouthSudan,
	SaoTomeandPrincipe,
	ElSalvador,
	Syrian,
	Swaziland,
	TurksandCaicosIslands,
	Chad,
	FrenchSouthernTerritories,
	Togo,
	Thailand,
	Tajikistan,
	Tokelau,
	TimorLeste,
	Turkmenistan,
	Tunisia,
	Tonga,
	Turkey,
	TrinidadandTobago,
	Tuvalu,
	Taiwan,
	Tanzania,
	Ukraine,
	Uganda,
	UnitedStates,
	Uruguay,
	Uzbekistan,
	HolySeeVaticanCityState,
	SaintVincent,
	Venezuela,
	VirginIslandBritish,
	VirginIslandsUS,
	Vietnam,
	Vanuatu,
	WallisandFutuna,
	Samoa,
	Yemen,
	Mayott,
	SouthAfrica,
	Zambia,
	Zimbabwe,
}

export interface INationalities {
	id: TypeNationalitesEnum;
	name: string;
	flagsCode: string;
}

export const typeNationalites: INationalities[] = [
	{
		id: TypeNationalitesEnum.Andorra,
		name: 'Andorra',
		flagsCode: 'AD',
	},
	{
		id: TypeNationalitesEnum.UnitedArabEmirates,
		name: 'United Arab Emirates',
		flagsCode: 'AE',
	},
	{
		id: TypeNationalitesEnum.Afghanistan,
		name: 'Afghanistan',
		flagsCode: 'AF',
	},
	{
		id: TypeNationalitesEnum.AntiguaandBarbuda,
		name: 'Antigua and Barbuda',
		flagsCode: 'AG',
	},
	{
		id: TypeNationalitesEnum.Anguilla,
		name: 'Anguilla',
		flagsCode: 'AI',
	},
	{
		id: TypeNationalitesEnum.Albania,
		name: 'Albania',
		flagsCode: 'AL',
	},
	{
		id: TypeNationalitesEnum.Armenia,
		name: 'Armenia',
		flagsCode: 'AM',
	},
	{
		id: TypeNationalitesEnum.NetherlandsAntilles,
		name: 'Netherlands Antilles',
		flagsCode: 'AN',
	},
	{
		id: TypeNationalitesEnum.Angola,
		name: 'Angola',
		flagsCode: 'AO',
	},
	{
		id: TypeNationalitesEnum.Antarctica,
		name: 'Antarctica',
		flagsCode: 'AQ',
	},
	{
		id: TypeNationalitesEnum.Argentina,
		name: 'Argentina',
		flagsCode: 'AR',
	},
	{
		id: TypeNationalitesEnum.AmericanSamoa,
		name: 'American Samoa',
		flagsCode: 'AS',
	},
	{
		id: TypeNationalitesEnum.Austria,
		name: 'Austria',
		flagsCode: 'AT',
	},
	{
		id: TypeNationalitesEnum.Australia,
		name: 'Australia',
		flagsCode: 'AU',
	},
	{
		id: TypeNationalitesEnum.Aruba,
		name: 'Aruba',
		flagsCode: 'AW',
	},
	{
		id: TypeNationalitesEnum.ÅlandIslands,
		name: 'Åland Islands',
		flagsCode: 'AX',
	},
	{
		id: TypeNationalitesEnum.Azerbaijan,
		name: 'Azerbaijan',
		flagsCode: 'AZ',
	},
	{
		id: TypeNationalitesEnum.BosniaandHerzegovina,
		name: 'Bosniaand Herzegovina',
		flagsCode: 'BA',
	},
	{
		id: TypeNationalitesEnum.Barbados,
		name: 'Barbados',
		flagsCode: 'BB',
	},
	{
		id: TypeNationalitesEnum.Bangladesh,
		name: 'Bangladesh',
		flagsCode: 'BD',
	},
	{
		id: TypeNationalitesEnum.Belgium,
		name: 'Belgium',
		flagsCode: 'BE',
	},
	{
		id: TypeNationalitesEnum.BurkinaFaso,
		name: 'Burkina Faso',
		flagsCode: 'BF',
	},
	{
		id: TypeNationalitesEnum.Bulgaria,
		name: 'Bulgaria',
		flagsCode: 'BG',
	},
	{
		id: TypeNationalitesEnum.Bahrain,
		name: 'Bahrain',
		flagsCode: 'BH',
	},
	{
		id: TypeNationalitesEnum.Burundi,
		name: 'Burundi',
		flagsCode: 'BI',
	},
	{
		id: TypeNationalitesEnum.Benin,
		name: 'Benin',
		flagsCode: 'BJ',
	},
	{
		id: TypeNationalitesEnum.SaintBarthélemy,
		name: 'Saint Barthélemy',
		flagsCode: 'BL',
	},
	{
		id: TypeNationalitesEnum.Bermuda,
		name: 'Bermuda',
		flagsCode: 'BM',
	},
	{
		id: TypeNationalitesEnum.BruneiDarussalam,
		name: 'Brunei Darussalam',
		flagsCode: 'BN',
	},
	{
		id: TypeNationalitesEnum.Bolivia,
		name: 'Bolivia',
		flagsCode: 'BO',
	},
	{
		id: TypeNationalitesEnum.SintEustatiusandSaba,
		name: 'Bonaire, Sint Eustatius and Saba',
		flagsCode: 'BQ',
	},
	{
		id: TypeNationalitesEnum.Brazil,
		name: 'Brazil',
		flagsCode: 'BR',
	},
	{
		id: TypeNationalitesEnum.Bahamas,
		name: 'Bahamas',
		flagsCode: 'BS',
	},
	{
		id: TypeNationalitesEnum.Bhutan,
		name: 'Bhutan',
		flagsCode: 'BT',
	},
	{
		id: TypeNationalitesEnum.BouvetIsland,
		name: 'Bouvet Island',
		flagsCode: 'BV',
	},
	{
		id: TypeNationalitesEnum.Botswana,
		name: 'Botswana',
		flagsCode: 'BW',
	},
	{
		id: TypeNationalitesEnum.Belarus,
		name: 'Belarus',
		flagsCode: 'BY',
	},
	{
		id: TypeNationalitesEnum.Belize,
		name: 'Belize',
		flagsCode: 'BZ',
	},
	{
		id: TypeNationalitesEnum.Canada,
		name: 'Canada',
		flagsCode: 'CA',
	},
	{
		id: TypeNationalitesEnum.CocosKeelingIslands,
		name: 'Cocos Keeling Islands',
		flagsCode: 'CC',
	},
	{
		id: TypeNationalitesEnum.CongoRepublic,
		name: 'Congo Democratic Republic',
		flagsCode: 'CD',
	},
	{
		id: TypeNationalitesEnum.African,
		name: 'African',
		flagsCode: 'CF',
	},
	{
		id: TypeNationalitesEnum.Congo,
		name: 'Congo',
		flagsCode: 'CG',
	},
	{
		id: TypeNationalitesEnum.Switzerland,
		name: 'Switzerland',
		flagsCode: 'CH',
	},
	{
		id: TypeNationalitesEnum.CôteDIvoire,
		name: 'Côte DIvoire',
		flagsCode: 'CI',
	},
	{
		id: TypeNationalitesEnum.CookIslands,
		name: 'Cook Islands',
		flagsCode: 'CK',
	},
	{
		id: TypeNationalitesEnum.Chile,
		name: 'Chile',
		flagsCode: 'CL',
	},
	{
		id: TypeNationalitesEnum.Cameroon,
		name: 'Cameroon',
		flagsCode: 'CM',
	},
	{
		id: TypeNationalitesEnum.China,
		name: 'China',
		flagsCode: 'CN',
	},
	{
		id: TypeNationalitesEnum.Colombia,
		name: 'Colombia',
		flagsCode: 'CO',
	},
	{
		id: TypeNationalitesEnum.CostaRica,
		name: 'Costa Rica',
		flagsCode: 'CR',
	},
	{
		id: TypeNationalitesEnum.Cuba,
		name: 'Cuba',
		flagsCode: 'CU',
	},
	{
		id: TypeNationalitesEnum.CapeVerde,
		name: 'Cape Verde',
		flagsCode: 'CV',
	},
	{
		id: TypeNationalitesEnum.Curaçao,
		name: 'Curaçao',
		flagsCode: 'CW',
	},
	{
		id: TypeNationalitesEnum.ChristmasIsland,
		name: 'Christmas Island',
		flagsCode: 'CX',
	},
	{
		id: TypeNationalitesEnum.Cyprus,
		name: 'Cyprus',
		flagsCode: 'CY',
	},
	{
		id: TypeNationalitesEnum.CzechRepublic,
		name: 'Czech Republic',
		flagsCode: 'CZ',
	},
	{
		id: TypeNationalitesEnum.Germany,
		name: 'Germany',
		flagsCode: 'DE',
	},
	{
		id: TypeNationalitesEnum.Djibouti,
		name: 'Djibouti',
		flagsCode: 'DJ',
	},
	{
		id: TypeNationalitesEnum.Denmark,
		name: 'Denmark',
		flagsCode: 'DK',
	},
	{
		id: TypeNationalitesEnum.Dominica,
		name: 'Dominica',
		flagsCode: 'DM',
	},
	{
		id: TypeNationalitesEnum.DominicanRepublic,
		name: 'Dominican Republic',
		flagsCode: 'DO',
	},
	{
		id: TypeNationalitesEnum.Algeria,
		name: 'Algeria',
		flagsCode: 'DZ',
	},
	{
		id: TypeNationalitesEnum.Ecuador,
		name: 'Ecuador',
		flagsCode: 'EC',
	},
	{
		id: TypeNationalitesEnum.Estonia,
		name: 'Estonia',
		flagsCode: 'EE',
	},
	{
		id: TypeNationalitesEnum.Egypt,
		name: 'Egypt',
		flagsCode: 'EG',
	},
	{
		id: TypeNationalitesEnum.WesternSahara,
		name: 'Western Sahara',
		flagsCode: 'EH',
	},
	{
		id: TypeNationalitesEnum.Eritrea,
		name: 'Eritrea',
		flagsCode: 'ER',
	},
	{
		id: TypeNationalitesEnum.España,
		name: 'España',
		flagsCode: 'ES',
	},
	{
		id: TypeNationalitesEnum.Ethiopia,
		name: 'Ethiopia',
		flagsCode: 'ET',
	},
	{
		id: TypeNationalitesEnum.Finland,
		name: 'Finlan',
		flagsCode: 'FI',
	},
	{
		id: TypeNationalitesEnum.Fiji,
		name: 'Fiji',
		flagsCode: 'FJ',
	},
	{
		id: TypeNationalitesEnum.FalklandIslandsMalvinas,
		name: 'Falkland Islands (Malvinas)',
		flagsCode: 'FK',
	},
	{
		id: TypeNationalitesEnum.Micronesia,
		name: 'Micronesia',
		flagsCode: 'FM',
	},
	{
		id: TypeNationalitesEnum.FaroeIslands,
		name: 'Faroe Islands',
		flagsCode: 'FO',
	},
	{
		id: TypeNationalitesEnum.France,
		name: 'France',
		flagsCode: 'FR',
	},
	{
		id: TypeNationalitesEnum.Gabon,
		name: 'Gabon',
		flagsCode: 'GA',
	},
	{
		id: TypeNationalitesEnum.UnitedKingdom,
		name: 'United Kingdom',
		flagsCode: 'GB',
	},
	{
		id: TypeNationalitesEnum.Grenada,
		name: 'Grenada',
		flagsCode: 'GD',
	},
	{
		id: TypeNationalitesEnum.Georgia,
		name: 'Georgia',
		flagsCode: 'GE',
	},
	{
		id: TypeNationalitesEnum.FrenchGuiana,
		name: 'French Guiana',
		flagsCode: 'GF',
	},
	{
		id: TypeNationalitesEnum.Guernsey,
		name: 'Guernsey',
		flagsCode: 'GG',
	},
	{
		id: TypeNationalitesEnum.Ghana,
		name: 'Ghana',
		flagsCode: 'GH',
	},
	{
		id: TypeNationalitesEnum.Gibraltar,
		name: 'Gibraltar',
		flagsCode: 'GI',
	},
	{
		id: TypeNationalitesEnum.Greenland,
		name: 'Greenland',
		flagsCode: 'GL',
	},
	{
		id: TypeNationalitesEnum.Gambia,
		name: 'Gambia',
		flagsCode: 'GM',
	},
	{
		id: TypeNationalitesEnum.Guinea,
		name: 'Guinea',
		flagsCode: 'GN',
	},
	{
		id: TypeNationalitesEnum.Guadeloupe,
		name: 'Guadeloupe',
		flagsCode: 'GP',
	},
	{
		id: TypeNationalitesEnum.EquatorialGuinea,
		name: 'Equatorial Guinea',
		flagsCode: 'GQ',
	},
	{
		id: TypeNationalitesEnum.Greece,
		name: 'Greece',
		flagsCode: 'GR',
	},
	{
		id: TypeNationalitesEnum.SouthGeorgia,
		name: 'South Georgia ',
		flagsCode: 'GS',
	},
	{
		id: TypeNationalitesEnum.Guatemala,
		name: 'Guatemala',
		flagsCode: 'GT',
	},
	{
		id: TypeNationalitesEnum.Guam,
		name: 'Guam',
		flagsCode: 'GU',
	},
	{
		id: TypeNationalitesEnum.GuineaBissau,
		name: 'Guinea-Bissau',
		flagsCode: 'GW',
	},
	{
		id: TypeNationalitesEnum.Guyana,
		name: 'Guyana',
		flagsCode: 'GY',
	},
	{
		id: TypeNationalitesEnum.HongKong,
		name: 'Hong Kong',
		flagsCode: 'HK',
	},
	{
		id: TypeNationalitesEnum.Honduras,
		name: 'Honduras',
		flagsCode: 'HN',
	},
	{
		id: TypeNationalitesEnum.Croatia,
		name: 'Croatia',
		flagsCode: 'HR',
	},
	{
		id: TypeNationalitesEnum.Haiti,
		name: 'Haiti',
		flagsCode: 'HT',
	},
	{
		id: TypeNationalitesEnum.Hungary,
		name: 'Hungary',
		flagsCode: 'HU',
	},
	{
		id: TypeNationalitesEnum.Indonesia,
		name: 'Indonesia',
		flagsCode: 'ID',
	},
	{
		id: TypeNationalitesEnum.Ireland,
		name: 'Ireland',
		flagsCode: 'IE',
	},
	{
		id: TypeNationalitesEnum.Israel,
		name: 'Israel',
		flagsCode: 'IL',
	},
	{
		id: TypeNationalitesEnum.IsleofMan,
		name: 'Isle of Man',
		flagsCode: 'IM',
	},
	{
		id: TypeNationalitesEnum.India,
		name: 'India',
		flagsCode: 'IN',
	},
	{
		id: TypeNationalitesEnum.BritishIndian,
		name: 'British Indian',
		flagsCode: 'IO',
	},
	{
		id: TypeNationalitesEnum.Iraq,
		name: 'Iraq',
		flagsCode: 'IQ',
	},
	{
		id: TypeNationalitesEnum.IranIslamic,
		name: 'Iran, Islamic',
		flagsCode: 'IR',
	},
	{
		id: TypeNationalitesEnum.Iceland,
		name: 'Iceland',
		flagsCode: 'IS',
	},
	{
		id: TypeNationalitesEnum.Italy,
		name: 'Italy',
		flagsCode: 'IT',
	},
	{
		id: TypeNationalitesEnum.Jersey,
		name: 'Jersey',
		flagsCode: 'JE',
	},
	{
		id: TypeNationalitesEnum.Jamaica,
		name: 'Jamaica',
		flagsCode: 'JM',
	},
	{
		id: TypeNationalitesEnum.Jordan,
		name: 'Jordan',
		flagsCode: 'JO',
	},
	{
		id: TypeNationalitesEnum.Japan,
		name: 'Japan',
		flagsCode: 'JP',
	},
	{
		id: TypeNationalitesEnum.Kenya,
		name: 'Kenya',
		flagsCode: 'KE',
	},
	{
		id: TypeNationalitesEnum.Kyrgyzstan,
		name: 'Kyrgyzstan',
		flagsCode: 'KG',
	},
	{
		id: TypeNationalitesEnum.Cambodia,
		name: 'Cambodia',
		flagsCode: 'KH',
	},
	{
		id: TypeNationalitesEnum.Kiribati,
		name: 'Kiribati',
		flagsCode: 'KI',
	},
	{
		id: TypeNationalitesEnum.Comoros,
		name: 'Comoros',
		flagsCode: 'KM',
	},
	{
		id: TypeNationalitesEnum.SaintKittsAndNevis,
		name: 'Saint Kitts And Nevis',
		flagsCode: 'KN',
	},
	{
		id: TypeNationalitesEnum.KoreaDemocratic,
		name: 'Korea, Democratic',
		flagsCode: 'KP',
	},
	{
		id: TypeNationalitesEnum.KoreaRepublic,
		name: 'Korea Republic',
		flagsCode: 'KR',
	},
	{
		id: TypeNationalitesEnum.Kuwait,
		name: 'Kuwait',
		flagsCode: 'KW',
	},
	{
		id: TypeNationalitesEnum.CaymanIslands,
		name: 'Cayman Islands',
		flagsCode: 'KY',
	},
	{
		id: TypeNationalitesEnum.Kazakhstan,
		name: 'Kazakhstan',
		flagsCode: 'KZ',
	},
	{
		id: TypeNationalitesEnum.LaoPeoples,
		name: 'Lao Peoples',
		flagsCode: 'LA',
	},
	{
		id: TypeNationalitesEnum.Lebanon,
		name: 'Lebanon',
		flagsCode: 'LB',
	},
	{
		id: TypeNationalitesEnum.SaintLucia,
		name: 'Saint Lucia',
		flagsCode: 'LC',
	},
	{
		id: TypeNationalitesEnum.Liechtenstein,
		name: 'Liechtenstein',
		flagsCode: 'LI',
	},
	{
		id: TypeNationalitesEnum.SriLanka,
		name: 'Sri Lanka',
		flagsCode: 'LK',
	},
	{
		id: TypeNationalitesEnum.Liberia,
		name: 'Liberia',
		flagsCode: 'LR',
	},
	{
		id: TypeNationalitesEnum.Lesotho,
		name: 'Lesotho',
		flagsCode: 'LS',
	},
	{
		id: TypeNationalitesEnum.Lithuania,
		name: 'Lithuania',
		flagsCode: 'LT',
	},
	{
		id: TypeNationalitesEnum.Luxembourg,
		name: 'Luxembourg',
		flagsCode: 'LU',
	},
	{
		id: TypeNationalitesEnum.Latvia,
		name: 'Latvia',
		flagsCode: 'LV',
	},
	{
		id: TypeNationalitesEnum.Libya,
		name: 'Libya',
		flagsCode: 'LY',
	},
	{
		id: TypeNationalitesEnum.Morocco,
		name: 'Morocco',
		flagsCode: 'MA',
	},
	{
		id: TypeNationalitesEnum.Monaco,
		name: 'Monaco',
		flagsCode: 'MC',
	},
	{
		id: TypeNationalitesEnum.MoldovaRepublic,
		name: 'Moldova Republic',
		flagsCode: 'MD',
	},
	{
		id: TypeNationalitesEnum.Montenegro,
		name: 'Montenegro',
		flagsCode: 'ME',
	},
	{
		id: TypeNationalitesEnum.SaintMartin,
		name: 'Saint Martin',
		flagsCode: 'MF',
	},
	{
		id: TypeNationalitesEnum.Madagascar,
		name: 'Madagascar',
		flagsCode: 'MG',
	},
	{
		id: TypeNationalitesEnum.MarshallIslands,
		name: 'Marshall Islands',
		flagsCode: 'MH',
	},
	{
		id: TypeNationalitesEnum.Macedonia,
		name: 'Macedonia',
		flagsCode: 'MK',
	},
	{
		id: TypeNationalitesEnum.Mali,
		name: 'Mali',
		flagsCode: 'ML',
	},
	{
		id: TypeNationalitesEnum.Myanmar,
		name: 'Myanmar',
		flagsCode: 'MM',
	},
	{
		id: TypeNationalitesEnum.Mongolia,
		name: 'Mongolia',
		flagsCode: 'MN',
	},
	{
		id: TypeNationalitesEnum.Macao,
		name: 'Macao',
		flagsCode: 'MO',
	},
	{
		id: TypeNationalitesEnum.NorthernMarianaIslands,
		name: 'Northern Mariana Islands',
		flagsCode: 'MP',
	},
	{
		id: TypeNationalitesEnum.Martinique,
		name: 'Martinique',
		flagsCode: 'MQ',
	},
	{
		id: TypeNationalitesEnum.Mauritania,
		name: 'Mauritania',
		flagsCode: 'MR',
	},
	{
		id: TypeNationalitesEnum.Montserrat,
		name: 'Montserrat',
		flagsCode: 'MS',
	},
	{
		id: TypeNationalitesEnum.Malta,
		name: 'Malta',
		flagsCode: 'MT',
	},
	{
		id: TypeNationalitesEnum.Mauritius,
		name: 'Mauritius',
		flagsCode: 'MU',
	},
	{
		id: TypeNationalitesEnum.Maldives,
		name: 'Maldives',
		flagsCode: 'MV',
	},
	{
		id: TypeNationalitesEnum.Malawi,
		name: 'Malawi',
		flagsCode: 'MW',
	},
	{
		id: TypeNationalitesEnum.Mexico,
		name: 'Mexico',
		flagsCode: 'MX',
	},
	{
		id: TypeNationalitesEnum.Malaysia,
		name: 'Malaysia',
		flagsCode: 'MY',
	},
	{
		id: TypeNationalitesEnum.Mozambique,
		name: 'Mozambique',
		flagsCode: 'MZ',
	},
	{
		id: TypeNationalitesEnum.Namibia,
		name: 'Namibia',
		flagsCode: 'NA',
	},
	{
		id: TypeNationalitesEnum.NewCaledonia,
		name: 'New Caledonia',
		flagsCode: 'NC',
	},
	{
		id: TypeNationalitesEnum.Niger,
		name: 'Niger',
		flagsCode: 'NE',
	},
	{
		id: TypeNationalitesEnum.NorfolkIsland,
		name: 'Norfolk Island',
		flagsCode: 'NF',
	},
	{
		id: TypeNationalitesEnum.Nigeria,
		name: 'Nigeria',
		flagsCode: 'NG',
	},
	{
		id: TypeNationalitesEnum.Nicaragua,
		name: 'Nicaragua',
		flagsCode: 'NI',
	},
	{
		id: TypeNationalitesEnum.NewZealand,
		name: 'New Zealand',
		flagsCode: 'NZ',
	},
	{
		id: TypeNationalitesEnum.Oman,
		name: 'Oman',
		flagsCode: 'OM',
	},
	{
		id: TypeNationalitesEnum.Panama,
		name: 'Panama',
		flagsCode: 'PA',
	},
	{
		id: TypeNationalitesEnum.Peru,
		name: 'Peru',
		flagsCode: 'PE',
	},
	{
		id: TypeNationalitesEnum.FrenchPolynesia,
		name: 'French Polynesia',
		flagsCode: 'PF',
	},
	{
		id: TypeNationalitesEnum.PapuaNewGuinea,
		name: 'Papua New Guinea',
		flagsCode: 'PG',
	},
	{
		id: TypeNationalitesEnum.Philippines,
		name: 'Philippines',
		flagsCode: 'PH',
	},
	{
		id: TypeNationalitesEnum.Pakistan,
		name: 'Pakistan',
		flagsCode: 'PK',
	},
	{
		id: TypeNationalitesEnum.Poland,
		name: 'Poland',
		flagsCode: 'PL',
	},
	{
		id: TypeNationalitesEnum.Pitcairn,
		name: 'Pitcairn',
		flagsCode: 'PN',
	},
	{
		id: TypeNationalitesEnum.PuertoRico,
		name: 'Puerto Rico',
		flagsCode: 'PR',
	},
	{
		id: TypeNationalitesEnum.Palestine,
		name: 'Palestine',
		flagsCode: 'PS',
	},
	{
		id: TypeNationalitesEnum.Portugal,
		name: 'Portugal',
		flagsCode: 'PT',
	},
	{
		id: TypeNationalitesEnum.Palau,
		name: 'Palau',
		flagsCode: 'PW',
	},
	{
		id: TypeNationalitesEnum.Paraguay,
		name: 'Paraguay',
		flagsCode: 'PY',
	},
	{
		id: TypeNationalitesEnum.Qatar,
		name: 'Qatar',
		flagsCode: 'QA',
	},
	{
		id: TypeNationalitesEnum.Réunion,
		name: 'Réunion',
		flagsCode: 'RE',
	},
	{
		id: TypeNationalitesEnum.Romania,
		name: 'Romania',
		flagsCode: 'RO',
	},
	{
		id: TypeNationalitesEnum.Serbia,
		name: 'Serbia',
		flagsCode: 'RS',
	},
	{
		id: TypeNationalitesEnum.RussianFederation,
		name: 'Russian Federation',
		flagsCode: 'RU',
	},
	{
		id: TypeNationalitesEnum.Rwanda,
		name: 'Rwanda',
		flagsCode: 'RW',
	},
	{
		id: TypeNationalitesEnum.SaudiArabia,
		name: 'Saudi Arabia',
		flagsCode: 'SA',
	},
	{
		id: TypeNationalitesEnum.SolomonIslands,
		name: 'Solomon Islands',
		flagsCode: 'SB',
	},
	{
		id: TypeNationalitesEnum.Seychelles,
		name: 'Seychelles',
		flagsCode: 'SC',
	},
	{
		id: TypeNationalitesEnum.Sudan,
		name: 'Sudan',
		flagsCode: 'SD',
	},
	{
		id: TypeNationalitesEnum.Sweden,
		name: 'Sweden',
		flagsCode: 'SE',
	},
	{
		id: TypeNationalitesEnum.Singapore,
		name: 'Singapore',
		flagsCode: 'SG',
	},
	{
		id: TypeNationalitesEnum.SaintHelena,
		name: 'Saint Helena',
		flagsCode: 'SH',
	},
	{
		id: TypeNationalitesEnum.Slovenia,
		name: 'Slovenia',
		flagsCode: 'SI',
	},
	{
		id: TypeNationalitesEnum.Slovakia,
		name: 'lovakia',
		flagsCode: 'SK',
	},
	{
		id: TypeNationalitesEnum.SierraLeone,
		name: 'ierra Leone',
		flagsCode: 'SL',
	},
	{
		id: TypeNationalitesEnum.SanMarino,
		name: 'San Marino',
		flagsCode: 'SM',
	},
	{
		id: TypeNationalitesEnum.Senegal,
		name: 'Senegal',
		flagsCode: 'SN',
	},
	{
		id: TypeNationalitesEnum.Somalia,
		name: 'Somalia',
		flagsCode: 'SO',
	},
	{
		id: TypeNationalitesEnum.Suriname,
		name: 'Suriname',
		flagsCode: 'SR',
	},
	{
		id: TypeNationalitesEnum.SouthSudan,
		name: 'South Sudan',
		flagsCode: 'SS',
	},
	{
		id: TypeNationalitesEnum.SaoTomeandPrincipe,
		name: 'Sao Tome and Principe',
		flagsCode: 'ST',
	},
	{
		id: TypeNationalitesEnum.ElSalvador,
		name: 'El Salvador',
		flagsCode: 'SV',
	},
	{
		id: TypeNationalitesEnum.Syrian,
		name: 'Syrian Arab Republic',
		flagsCode: 'SY',
	},
	{
		id: TypeNationalitesEnum.Swaziland,
		name: 'Swaziland',
		flagsCode: 'SZ',
	},
	{
		id: TypeNationalitesEnum.TurksandCaicosIslands,
		name: 'Turks and Caicos Islands',
		flagsCode: 'TC',
	},
	{
		id: TypeNationalitesEnum.Chad,
		name: 'Chad',
		flagsCode: 'TD',
	},
	{
		id: TypeNationalitesEnum.FrenchSouthernTerritories,
		name: 'French Southern Territories',
		flagsCode: 'TF',
	},
	{
		id: TypeNationalitesEnum.Togo,
		name: 'Togo',
		flagsCode: 'TG',
	},
	{
		id: TypeNationalitesEnum.Thailand,
		name: 'Thailand',
		flagsCode: 'TH',
	},
	{
		id: TypeNationalitesEnum.Tajikistan,
		name: 'Tajikistan',
		flagsCode: 'TJ',
	},
	{
		id: TypeNationalitesEnum.Tokelau,
		name: 'Tokelau',
		flagsCode: 'TK',
	},
	{
		id: TypeNationalitesEnum.TimorLeste,
		name: 'Timor-Leste',
		flagsCode: 'TL',
	},
	{
		id: TypeNationalitesEnum.Turkmenistan,
		name: 'Turkmenistan',
		flagsCode: 'TM',
	},
	{
		id: TypeNationalitesEnum.Tunisia,
		name: 'Tunisia',
		flagsCode: 'TN',
	},
	{
		id: TypeNationalitesEnum.Tonga,
		name: 'Tonga',
		flagsCode: 'TO',
	},
	{
		id: TypeNationalitesEnum.Turkey,
		name: 'Turkey',
		flagsCode: 'TR',
	},
	{
		id: TypeNationalitesEnum.TrinidadandTobago,
		name: 'Trinidad and Tobago',
		flagsCode: 'TT',
	},
	{
		id: TypeNationalitesEnum.Tuvalu,
		name: 'Tuvalu',
		flagsCode: 'TV',
	},
	{
		id: TypeNationalitesEnum.Taiwan,
		name: 'Taiwan',
		flagsCode: 'TW',
	},
	{
		id: TypeNationalitesEnum.Tanzania,
		name: 'Tanzania',
		flagsCode: 'TZ',
	},
	{
		id: TypeNationalitesEnum.Ukraine,
		name: 'Ukraine',
		flagsCode: 'UA',
	},
	{
		id: TypeNationalitesEnum.Uganda,
		name: 'Uganda',
		flagsCode: 'UG',
	},
	{
		id: TypeNationalitesEnum.UnitedStates,
		name: 'United States',
		flagsCode: 'US',
	},
	{
		id: TypeNationalitesEnum.Uruguay,
		name: 'Uruguay',
		flagsCode: 'UY',
	},
	{
		id: TypeNationalitesEnum.Uzbekistan,
		name: 'Uzbekistan',
		flagsCode: 'UZ',
	},
	{
		id: TypeNationalitesEnum.HolySeeVaticanCityState,
		name: 'Holy See (Vatican City State)',
		flagsCode: 'VA',
	},
	{
		id: TypeNationalitesEnum.SaintVincent,
		name: 'Saint Vincent ',
		flagsCode: 'VC',
	},
	{
		id: TypeNationalitesEnum.Venezuela,
		name: 'Venezuela',
		flagsCode: 'VE',
	},
	{
		id: TypeNationalitesEnum.VirginIslandBritish,
		name: 'Virgin Islands, British',
		flagsCode: 'VG',
	},
	{
		id: TypeNationalitesEnum.VirginIslandsUS,
		name: 'Virgin Islands, U.S',
		flagsCode: 'VI',
	},
	{
		id: TypeNationalitesEnum.Vietnam,
		name: 'Vietnam',
		flagsCode: 'VN',
	},
	{
		id: TypeNationalitesEnum.Vanuatu,
		name: 'Vanuatu',
		flagsCode: 'VU',
	},
	{
		id: TypeNationalitesEnum.WallisandFutuna,
		name: 'Wallis and Futuna',
		flagsCode: 'WF',
	},
	{
		id: TypeNationalitesEnum.Samoa,
		name: 'Samoa',
		flagsCode: 'WS',
	},
	{
		id: TypeNationalitesEnum.Yemen,
		name: 'Yemen',
		flagsCode: 'YE',
	},
	{
		id: TypeNationalitesEnum.Mayott,
		name: 'Mayott',
		flagsCode: 'YT',
	},
	{
		id: TypeNationalitesEnum.SouthAfrica,
		name: 'South Africa',
		flagsCode: 'ZA',
	},
	{
		id: TypeNationalitesEnum.Zambia,
		name: 'Zambia',
		flagsCode: 'ZM',
	},
	{
		id: TypeNationalitesEnum.Zimbabwe,
		name: 'Zimbabwe',
		flagsCode: 'ZW',
	},
];

export enum TypePersonEnum {
	NATURAL = 1,
	LEGAL,
}

export interface IPersonType {
	id: TypePersonEnum;
	name: string;
}

export const typePerson: IPersonType[] = [
	{
		id: TypePersonEnum.LEGAL,
		name: 'LEGAL',
	},
	{
		id: TypePersonEnum.NATURAL,
		name: 'NATURAL',
	},
];

export enum TypeCustomerPaymentEnum {
	creditCard,
	cash,
	bank,
}

export interface ICustomerPaymentType {
	id: TypeCustomerPaymentEnum;
	name: string;
}

export const typePayment: ICustomerPaymentType[] = [
	{
		id: TypeCustomerPaymentEnum.creditCard,
		name: 'Tarjeta de credito',
	},
	{
		id: TypeCustomerPaymentEnum.cash,
		name: 'Efectivo',
	},
	{
		id: TypeCustomerPaymentEnum.bank,
		name: 'Banco',
	},
];

export interface ICustomerPayments {
	customerPayments: ICustomerPayment[];
}

export interface ICustomerPayment {
	paymentType: string;
}

export interface ICreditCardForm {
	cardHolderName: string;
	cardNumber: string;
	expirationDate: string;
	amount: number;
	transactionId: string;
	dateTime: string;
}

export interface ICashForm {
	amount: number;
	transactionId: string;
	dateTime: string;
}

export interface IBankForm {
	bankName: string;
	bankAccuntNumber: string;
	amount: number;
	transactionId: string;
	dateTime: string;
}

export interface ICustomer {
	id: string;
	person: string;
	mainPassengerId?: string;
	name: string;
	nationality: string;
	documents: IDocuments[];
}

export interface IProvider {
	id: string;
	fantasyName: string;
	businessName: string;
	country: ICountry;
	stateName: string;
	cityName: string;
	street: string;
	postalCode: string;
	rut: string;
	phone: string;
	email: string;
	enable: boolean;
}

export interface IAccommodationService extends IService {
	keyId: string;
	reference: string;
	tariffProvider?: IProvider | string;
	unit: ServiceUnitEnum;
	cost?: string;
	saleAmount?: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	serviceProvider?: AccomodationProviderItem;
	sameTariffServiceProvider: boolean;
	destination: ICodeName;
	startDate: string;
	endDate: string;
	limitDate: string;
	rooms: IRooms[];
	currencyRate?: number;
	totalSaleAmountInLocalCurrency?: number;
	issueRequest?: IIssueRequestDetail; //solo se envia en el detalle del acommodation no cuando se crea y es solo cuando se hace una solicitud de emision
	billingStatusDetail?: IServiceBillingStatusDetailDTO; //solo se envia en el detalle
	automaticProviderPaymentDateCalculation: boolean;
	providerManualPaymentDateLines: IServiceProviderManualPaymentDateLineDTO[];
	providerAutomaticPaymentDateLine?: IServiceProviderAutomaticPaymentDateInfoDTO; //solo se envia en el detalle
}

export interface IRooms {
	keyId: string;
	passengerIds: string[];
	tariffType: AccommodationFeeTypeEnum[];
	room: string;
	board: AccommodationRoomBoardEnum[];
	cost?: string;
	saleAmount?: string;
	markupPercentage?: string;
}

export enum AccommodationFeeTypeEnum {
	Refundable = 'Refundable',
	NonRefundable = 'NonRefundable',
}

export interface IAccommodationFeeType {
	id: AccommodationFeeTypeEnum | '';
	name: string;
}

export const accommodationFeeType: IAccommodationFeeType[] = [
	{
		id: '',
		name: 'No Aplica',
	},
	{
		id: AccommodationFeeTypeEnum.Refundable,
		name: 'Reembolsable',
	},
	{
		id: AccommodationFeeTypeEnum.NonRefundable,
		name: 'No reembolsable',
	},
];

export enum AccommodationRoomBoardEnum {
	Breakfast = 'Breakfast',
	OnlyRoom = 'OnlyRoom',
	HalfBoard = 'HalfBoard',
	FullBoard = 'FullBoard',
	AllInclusive = 'AllInclusive',
}

export interface IAccommodationRoomBoard {
	id: AccommodationRoomBoardEnum;
	name: string;
}

export const accommodationRoomBoard: IAccommodationRoomBoard[] = [
	{
		id: AccommodationRoomBoardEnum.OnlyRoom,
		name: 'Solo habitacion',
	},
	{
		id: AccommodationRoomBoardEnum.Breakfast,
		name: 'Desayuno Incluido',
	},
	{
		id: AccommodationRoomBoardEnum.HalfBoard,
		name: 'Media pensión',
	},
	{
		id: AccommodationRoomBoardEnum.FullBoard,
		name: 'Pensión completa',
	},
	{
		id: AccommodationRoomBoardEnum.AllInclusive,
		name: 'All inclusive',
	},
];

export interface IProviderAutocomplete {
	id: number;
	fantasyName: string;
	businessName: string;
	country: ICountry;
	stateName: string;
	cityName: string;
	street?: string;
	postalCode?: string;
	rut?: string;
	phone?: string;
	email?: string;
	enable: boolean;
}

export interface ICountry {
	code: string;
	name: string;
}

export interface ITicket {
	passengerId: string;
	ticket: string;
}

export enum PassengerTypeEnum {
	A = 'A',
	B = 'B',
	C = 'C',
	D = 'D',
}

export interface IServiceBillingStatusDetailDTO {
	netsuiteLineId: number;
	invoiceNumber?: string;
	netsuiteInvoiceId?: number;
	netsuiteInvoiceUrl?: string;
	invoiceIssueDate?: string;
	invoiceDueDate?: string;
	invoicePaymentTerms?: string;
	invoiceDocumentType?: string;
	hasPendingBilling: boolean;
	pendingBillingAmount: number;
	purchaseOrderNetsuiteId?: number;
	purchaseOrderNetsuiteUrl?: string;
	purchaseOrderNumber?: string;
	purchaseOrderStatus?: string;
	providerPaymentStatus?: string;
	providerBillingNumber?: string;
	providerBillingAmountUSD?: number;
	syncStatus: NetsuiteSyncStatusEnum;
	lastSuccessSyncAt?: string;
	lastError?: string;
	lastErrorAt?: string;
	lastActionExecutedBy: string;
}

export interface IServiceProviderManualPaymentDateLineDTO {
	id?: string | null;
	paymentDate: string; // yyyy-MM-dd
	amount: number | null;
	observation?: string | null;
}

export interface IServiceProviderAutomaticPaymentDateInfoDTO {
	paymentDate?: string | null;
	amount?: number | null;
	sourceDate?: string | null;
	errorMessage?: string | null;
	notCalculatedReason?: string | null;
}

export interface IFlightServicePassengersByPassengerType {
	passengerType: PassengerTypeEnum;
	passengerIds: string[];
	cost: number;
	markupPercentage?: number;
	saleAmount: number;
	totalCost: number;
	totalSaleAmount: number;
	passengersTickets: ITicket[];
}

export interface IIssueRequestDetail {
	caseId: number;
	created: string;
	createdBy: string;
}

export interface IFlightService {
	keyId: string;
	reference: string;
	tariffProvider?: string | IProvider;
	unit: ServiceUnitEnum;
	cost?: string;
	markupPercentage: string;
	saleAmount?: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	passengersGroupByPassengerType: IFlightServicePassengersByPassengerType[];
	gds?: GDSEnum[] | NotApplicable;
	vc: IValidatingCarrier[];
	oidIssueId?: number | string;
	limitDate: string;
	withTst?: string;
	tst: string;
	tqt: string;
	baggageType?: BaggageEnum;
	carryOn?: boolean;
	flightLegs: IFlightServiceLeg[];
	FXL: boolean;
	OPEROK: boolean;
	isReissue: boolean;
	currencyRate?: number;
	totalSaleAmountInLocalCurrency?: number;
	issueRequest?: IIssueRequestDetail; //solo se envia en el detalle del vuelo no cuando se crea y es solo cuando se hace una solicitud de emision
	ruleComment?: string; // sugerencia de emisión que viene de la API
	billingStatusDetail?: IServiceBillingStatusDetailDTO; //solo se envia en el detalle
	automaticProviderPaymentDateCalculation: boolean;
	providerManualPaymentDateLines: IServiceProviderManualPaymentDateLineDTO[];
	providerAutomaticPaymentDateLine?: IServiceProviderAutomaticPaymentDateInfoDTO; //solo se envia en el detalle
}

export interface IFlightServiceLeg {
	keyId: string;
	flightNumber: string;
	airline: IFlightServiceLegAirline;
	departure: IFlightServiceLegEvent;
	arrival: IFlightServiceLegEvent;
}

export interface IFlightServiceLegEvent {
	airport: IFlightLegAirport;
	date: string;
	hour: string;
}

export interface IFlightLegAirport {
	code: string;
	name: string;
	cityName: string;
}

export interface IFlightServiceLegAirline {
	image: string;
	code: string;
	name: string;
}

export enum BaggageEnum {
	OneWay1x23KG = 'OneWay1x23KG',
	OneWay2x23KG = 'OneWay2x23KG',
	OneWay1x32KG = 'OneWay1x32KG',
	OneWay2x32KG = 'OneWay2x32KG',

	OnlyReturn1x23KG = 'OnlyReturn1x23KG',
	OnlyReturn2x23KG = 'OnlyReturn2x23KG',
	OnlyReturn1x32KG = 'OnlyReturn1x32KG',
	OnlyReturn2x32KG = 'OnlyReturn2x32KG',

	RoundTrip1x23KG = 'RoundTrip1x23KG',
	RoundTrip2x23KG = 'RoundTrip2x23KG',
	RoundTrip1x32KG = 'RoundTrip1x32KG',
	RoundTrip2x32KG = 'RoundTrip2x32KG',

	None = 'None',
}

export enum GDSEnum {
	Amadeus = 'Amadeus',
	Sabre = 'Sabre',
	NDS = 'NDS',
	NDX = 'NDX',
	NDC = 'NDC',
	Starlings = 'Starlings',
	PlatformOfProvider = 'PlatformOfProvider',
}

export interface IGDSOption {
	id: GDSEnum | 'n/a';
	name: string;
}

export interface ISelectOption {
	id: string;
	name: string;
}

export enum ServiceUnitEnum {
	Person = 'Person',
	Group = 'Group',
	PerRoom = 'PerRoom',
	PerCabin = 'PerCabin',
	Vehicle = 'Vehicle',
}

export interface IUnitType {
	id: ServiceUnitEnum;
	name: string;
}

export const typeUnit: IUnitType[] = [
	{
		id: ServiceUnitEnum.Person,
		name: 'Persona',
	},
	{
		id: ServiceUnitEnum.Group,
		name: 'Grupo',
	},
	{
		id: ServiceUnitEnum.PerRoom,
		name: 'Habitacion',
	},
	{
		id: ServiceUnitEnum.PerCabin,
		name: 'Camarote',
	},
	{
		id: ServiceUnitEnum.Vehicle,
		name: 'Vehiculo',
	},
];

export enum FlightAddOnTypeEnum {
	Luggage = 'Luggage',
	PassengerAssistance = 'PassengerAssistance',
	Pet = 'Pet',
	Seat = 'Seat',
	SpecialFood = 'SpecialFood',
}

export interface IFlightAddOnType {
	id: FlightAddOnTypeEnum;
	name: string;
}

export const flightAddOnType: IFlightAddOnType[] = [
	{
		id: FlightAddOnTypeEnum.Luggage,
		name: 'Equipaje',
	},
	{
		id: FlightAddOnTypeEnum.PassengerAssistance,
		name: 'Asistencia al pasajero',
	},
	{
		id: FlightAddOnTypeEnum.Pet,
		name: 'Mascotas',
	},
	{
		id: FlightAddOnTypeEnum.Seat,
		name: 'Asientos',
	},
	{
		id: FlightAddOnTypeEnum.SpecialFood,
		name: 'Comida especial',
	},
];

export interface IServices {
	serviceType: TypeServiceEnum;
	keyId: string;
	enable?: boolean;
	services: (
		| IInsurance
		| ICircuitService
		| ICarRental
		| ITrainService
		| IActivityService
		| IFlightAddOm
		| IThirdPartyPackage
		| IFerry
		| IOtherService
		| IAccommodationService
	)[];
}

export interface IService {
	serviceType: TypeServiceEnum;
	enable?: boolean;
	keyId: string;
	serviceName: string;
}

export type NotApplicable = 'n/a';

export enum TypeServiceEnum {
	Flight = 'Flight',
	FlightAncillary = 'FlightAncillary',
	Accommodation = 'Accommodation',
	Transfer = 'Transfer',
	Insurance = 'Insurance',
	Circuit = 'Circuit',
	Train = 'Train',
	CarRental = 'CarRental',
	Bus = 'Bus',
	Activity = 'Activity',
	FlightAddOn = 'FlightAddOn',
	ThirdPartyPackage = 'ThirdPartyPackage',
	Ferry = 'Ferry',
	Cruise = 'Cruise',
	Other = 'Other',
	Fee = 'Fee',
}

export interface IServiceType {
	id: TypeServiceEnum;
	name: string;
	icon: JSX.Element;
}

export const serviceTypes: IServiceType[] = [
	{
		id: TypeServiceEnum.Flight,
		name: 'Vuelos',
		icon: <FlightIcon />,
	},
	{
		id: TypeServiceEnum.FlightAncillary,
		name: 'Adicionales de vuelo',
		icon: <LuggageIcon />,
	},
	{
		id: TypeServiceEnum.Accommodation,
		name: 'Alojamiento',
		icon: <HotelIcon />,
	},

	{
		id: TypeServiceEnum.Transfer,
		name: 'Traslados',
		icon: <AirportShuttleIcon />,
	},
	// TODO: keep removing comments and adding services
	// {
	// 	id: TypeServiceEnum.Insurance,
	// 	name: 'Seguros de viaje',
	// 	icon: <SecurityIcon />,
	// },
	// {
	// 	id: TypeServiceEnum.Circuit,
	// 	name: 'Circuito',
	// 	icon: <SignpostIcon />,
	// },
	// {
	// 	id: TypeServiceEnum.CarRental,
	// 	name: 'Alquiler de auto',
	// 	icon: <CarRentalIcon />,
	// },
	// {
	// 	id: TypeServiceEnum.Bus,
	// 	name: 'Bus',
	// 	icon: <DirectionsBusIcon />,
	// },
	// {
	// 	id: TypeServiceEnum.Train,
	// 	name: 'Tren',
	// 	icon: <TrainIcon />,
	// },
	// {
	// 	id: TypeServiceEnum.Activity,
	// 	name: 'Actividades',
	// 	icon: <LocalActivityIcon />,
	// },
	// {
	// 	id: TypeServiceEnum.FlightAddOn,
	// 	name: 'Adicionales de vuelo',
	// 	icon: <LuggageIcon />,
	// },
	// {
	// 	id: TypeServiceEnum.ThirdPartyPackage,
	// 	name: 'Paquetes de terceros',
	// 	icon: <GroupsIcon />,
	// },
	// {
	// 	id: TypeServiceEnum.Ferry,
	// 	name: 'Ferry',
	// 	icon: <HouseboatIcon />,
	// },
	// {
	// 	id: TypeServiceEnum.Cruise,
	// 	name: 'Crucero',
	// 	icon: <DirectionsBoatIcon />,
	// },
	{
		id: TypeServiceEnum.Other,
		name: 'Otros',
		icon: <ExtensionIcon />,
	},
	{
		id: TypeServiceEnum.Fee,
		name: 'Fee',
		icon: <AttachMoney />,
	},
];

export interface ITransferService extends IService {
	keyId: string;
	reference: string;
	tariffProvider?: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	saleAmountInLocalCurrency?: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	transferSegments?: ITransferSegment[];
	currencyRate?: number;
	totalSaleAmountInLocalCurrency?: number;
	billingStatusDetail?: IServiceBillingStatusDetailDTO; //solo se envia en el detalle
	automaticProviderPaymentDateCalculation: boolean;
	providerManualPaymentDateLines: IServiceProviderManualPaymentDateLineDTO[];
	providerAutomaticPaymentDateLine?: IServiceProviderAutomaticPaymentDateInfoDTO; //solo se envia en el detalle
}

export interface ITransferSegment {
	transferType: TransferTypeEnum[];
	originName: string;
	destinationName: string;
	startDate: string;
	endDate: string;
}

export enum TransferTypeEnum {
	In = 'In',
	InOut = 'InOut',
	InPrivate = 'InPrivate',
	InOutPrivate = 'InOutPrivate',
}

export interface ITypeTransfer {
	id: TransferTypeEnum;
	name: string;
}

export const typeTransfer: ITypeTransfer[] = [
	{
		id: TransferTypeEnum.In,
		name: 'Ida',
	},
	{
		id: TransferTypeEnum.InOut,
		name: 'Ida y vuelta',
	},
	{
		id: TransferTypeEnum.InPrivate,
		name: 'Ida privado',
	},
	{
		id: TransferTypeEnum.InOutPrivate,
		name: 'Ida y vuelta privado',
	},
];

export interface IInsurance extends IService {
	keyId: string;
	reference: string;
	serviceType: TypeServiceEnum;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	voucherNotes?: string;
	internalNotes?: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	serviceName: string;
	residenceCountry: ICodeName;
	startDate: string;
	endDate: string;
	limitDate: string;
}

export interface ICircuitService extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	serviceName: string;
	origin: ICodeName;
	endDestination: ICodeName;
	startDate: string;
	endDate: string;
	limitDate: string;
}

export interface ICarRental extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	destination: ICodeName;
	vehicleType: string;
	pickupLocation: string;
	pickupAt: string;
	dropOffLocation: string;
	dropOffAt: string;
	limitDate: string;
}

export interface IBusService extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	travelType: TravelTypeEnum;
	seatsDistribution?: ISeatsDistribution[];
	origin: ICodeName;
	destination: ICodeName;
	startDate: string;
	endDate: string;
	limitDate: string;
}

export interface ISeatsDistribution {
	passengerId: string;
	seatNumber: string;
	passengerName?: string;
}

export interface ITrainService extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	seatsDistribution: ISeatsDistribution[];
	voucherNotes?: string;
	internalNotes?: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	travelType: TravelTypeEnum;
	serviceClass: string;
	origin: ICodeName;
	destination: ICodeName;
	startDate: string;
	endDate: string;
	limitDate: string;
}

export interface IActivityService extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	currency: string;
	totalCost: string;
	totalSaleAmount: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	activityServiceType: string;
	serviceName: string;
	useDate: string;
	limitDate: string;
	destination: ICodeName;
}

export interface IFlightAddOm extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes: string;
	internalNotes: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	flightAddOnType: FlightAddOnTypeEnum[];
	serviceDetail: string;
}

export interface IThirdPartyPackage extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	packageName: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes: string;
	internalNotes: string;
	passengerIds: string[];
	destinations: ICodeName[];
	startDate: string;
	endDate: string;
	limitDate: string;
}

export interface IFerry extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	travelType: TravelTypeEnum;
	serviceClass: string;
	origin: ICodeName;
	destination: ICodeName;
	startDate: string;
	endDate: string;
	limitDate: string;
}

export enum ActivityServiceTypeEnum {
	Wanderings = 'Wanderings',
	TicketsOnly = 'TicketsOnly',
	TicketsAndTransfers = 'TicketsAndTransfers',
}

export interface IActivityType {
	id: ActivityServiceTypeEnum;
	name: string;
}

export const activityType: IActivityType[] = [
	{
		id: ActivityServiceTypeEnum.Wanderings,
		name: 'Paseo',
	},
	{
		id: ActivityServiceTypeEnum.TicketsOnly,
		name: 'Solo entradas',
	},
	{
		id: ActivityServiceTypeEnum.TicketsAndTransfers,
		name: 'Entradas y traslados',
	},
];

export enum TravelTypeEnum {
	OW = 'OW',
	RT = 'RT',
}

export interface ITravelType {
	id: TravelTypeEnum;
	name: string;
}

export const tripType: ITravelType[] = [
	{
		id: TravelTypeEnum.OW,
		name: 'Ida',
	},
	{
		id: TravelTypeEnum.RT,
		name: 'Ida y vuelta',
	},
];

export interface ICruiseService extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost?: string;
	saleAmount?: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	cruiseName: string;
	portShipment: string;
	portLanding: string;
	samePort: boolean;
	startDate: string;
	endDate: string;
	limitDate: string;
	rooms?: ICruiseServiceRoom[];
}

export interface ICruiseServiceRoom {
	keyId: string;
	passengerIds?: string[];
	distribution: CruiseRoomDistributionEnum;
	category: CruiseRoomCategoryEnum;
	board: CruiseRoomBoardEnum;
	cost?: string;
	markupPercentage?: string;
	saleAmount?: string;
	totalCost?: string;
	totalSaleAmount?: string;
}

export enum CruiseRoomDistributionEnum {
	Simple = 'Simple',
	Double = 'Double',
	Triple = 'Triple',
	Quadruple = 'Quadruple',
}

export interface ICruiseRoomDistribution {
	id: CruiseRoomDistributionEnum;
	name: string;
}

export const cruiseRoomDistribution: ICruiseRoomDistribution[] = [
	{
		id: CruiseRoomDistributionEnum.Simple,
		name: 'Simple',
	},
	{
		id: CruiseRoomDistributionEnum.Double,
		name: 'Doble',
	},
	{
		id: CruiseRoomDistributionEnum.Triple,
		name: 'Triple',
	},
	{
		id: CruiseRoomDistributionEnum.Quadruple,
		name: 'Cuadruple',
	},
];

export enum CruiseRoomCategoryEnum {
	Internal = 'Internal',
	Suite = 'Suite',
	WithBalcony = 'WithBalcony',
	WithView = 'WithView',
}

export interface ICruiseRoomCategory {
	id: CruiseRoomCategoryEnum;
	name: string;
}

export const cruiseRoomCategory: ICruiseRoomCategory[] = [
	{
		id: CruiseRoomCategoryEnum.Internal,
		name: 'Interno',
	},
	{
		id: CruiseRoomCategoryEnum.Suite,
		name: 'Suite',
	},
	{
		id: CruiseRoomCategoryEnum.WithBalcony,
		name: 'con balcon',
	},
	{
		id: CruiseRoomCategoryEnum.WithView,
		name: 'con vista',
	},
];

export enum CruiseRoomBoardEnum {
	AllInclusive = 'AllInclusive',
	FullBoard = 'FullBoard',
	HalfBoard = 'HalfBoard',
	OnlyRoom = 'OnlyRoom',
}

export interface ICruiseRoomBoard {
	id: CruiseRoomBoardEnum;
	name: string;
}

export const cruiseRoomBoard: ICruiseRoomBoard[] = [
	{
		id: CruiseRoomBoardEnum.AllInclusive,
		name: 'All inclusive',
	},
	{
		id: CruiseRoomBoardEnum.FullBoard,
		name: 'Pension completa',
	},
	{
		id: CruiseRoomBoardEnum.HalfBoard,
		name: 'Media pension',
	},
	{
		id: CruiseRoomBoardEnum.OnlyRoom,
		name: 'Solo alojamiento',
	},
];

export interface IOtherService extends IService {
	keyId: string;
	reference: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	saleAmountInLocalCurrency?: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	voucherNotes?: string;
	internalNotes?: string;
	passengerIds: string[];
	serviceProvider: IProvider;
	sameTariffServiceProvider: boolean;
	otherServiceType: string;
	serviceName: string;
	startDate: string;
	endDate: string;
	limitDate: string;
	currencyRate?: number;
	totalSaleAmountInLocalCurrency?: number;
	issueRequest?: IIssueRequestDetail; //solo se envia en el detalle del servicio no cuando se crea y es solo cuando se hace una solicitud de emision
	billingStatusDetail?: IServiceBillingStatusDetailDTO; //solo se envia en el detalle
	automaticProviderPaymentDateCalculation: boolean;
	providerManualPaymentDateLines: IServiceProviderManualPaymentDateLineDTO[];
	providerAutomaticPaymentDateLine?: IServiceProviderAutomaticPaymentDateInfoDTO; //solo se envia en el detalle
}

export interface IFeeService extends IService {
	keyId: string;
	tariffProvider: IProvider;
	unit: ServiceUnitEnum[];
	cost: string;
	saleAmount: string;
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	markupPercentage: string;
	serviceType: TypeServiceEnum;
	passengerIds: string[];
	feeServiceType: string;
	currencyRate?: number;
	saleAmountInLocalCurrency?: number;
	totalSaleAmountInLocalCurrency?: number;
	billingStatusDetail?: IServiceBillingStatusDetailDTO; //solo se envia en el detalle
	automaticProviderPaymentDateCalculation: boolean;
	providerManualPaymentDateLines: IServiceProviderManualPaymentDateLineDTO[];
	providerAutomaticPaymentDateLine?: IServiceProviderAutomaticPaymentDateInfoDTO; //solo se envia en el detalle
}

export interface ICostVariationLogs {
	serviceCostVariationLogs: IServiceCostVariationLogs[];
	saleOrderCostVariationLogs: ISaleOrderCostVariationLogs[];
}

export interface IServiceCostVariationLogs {
	id: string;
	serviceId: string;
	serviceType: TypeServiceEnum;
	saleAmount: number;
	saleAmountVariation: number;
	totalCost: number;
	totalCostVariation: number;
	grossProfit: number;
	grossProfitVariation: number;
	createdBy: string;
	created: string;
}

export interface ISaleOrderCostVariationLogs {
	id: string;
	saleOrderId: string;
	saleAmount: number;
	saleAmountVariation: number;
	totalCost: number;
	totalCostVariation: number;
	grossProfit: number;
	grossProfitVariation: number;
	dateCreated: string;
}

export interface ICustomersAutocomplete {
	customerId: string;
	customerName: string;
	email: string;
	phone?: string;
	taxIdentificationNumber?: string;
	countryCode: string;
	countryName: string;
}

export interface IChannel {
	id: number;
	name: string;
}

export interface State {
	id: number;
	name: string;
	countryCode: string;
}

export interface Province {
	id: number;
	name: string;
	stateId: number;
	countryCode: string;
}

export interface Municipality {
	id: number;
	name: string;
	stateId: number;
	provinceId: number;
	countryCode: string;
}

export interface NetsuitePlaces {
	states: State[];
	countries: {
		id: number;
		name: string;
	}[];
	provinces: Province[];
	municipalities: Municipality[];
}

export interface TaxDataItem {
	id: number;
	name: string;
	countryCode: 'CO' | 'BO' | 'UY' | 'PY';
	isNetsuiteDocument?: boolean;
	needVerificationNumber?: boolean;
	isAllowToJuridicPerson?: boolean;
	isAllowToPhysicalPersonInvoiceable?: boolean;
}

export interface NetsuiteTaxDataResponse {
	fiscalResponsibilities: TaxDataItem[];
	taxPayerTypes: TaxDataItem[];
	taxRegimes: TaxDataItem[];
	taxResidenceTypes: TaxDataItem[];
	documentTypes: TaxDataItem[];
}

export enum SaleOrderPendingPayMessageScheduleStatusEnum {
	Pending = 'Pending',
	Sent = 'Sent',
	Cancelled = 'Cancelled',
	Error = 'Error',
}

export const saleOrderPendingPayMessageScheduleStatusEnumWithName: Record<
	SaleOrderPendingPayMessageScheduleStatusEnum,
	{ name: string }
> = {
	[SaleOrderPendingPayMessageScheduleStatusEnum.Pending]: {
		name: 'Pendiente',
	},
	[SaleOrderPendingPayMessageScheduleStatusEnum.Sent]: {
		name: 'Enviado',
	},

	[SaleOrderPendingPayMessageScheduleStatusEnum.Cancelled]: {
		name: 'Cancelado',
	},
	[SaleOrderPendingPayMessageScheduleStatusEnum.Error]: {
		name: 'Error',
	},
};

export interface ISaleOrderPendingPayMessageScheduleList {
	dateToSend: string;
	status: SaleOrderPendingPayMessageScheduleStatusEnum;
	observation?: string;
	created: string;
	createdBy: string;
	executed?: string;
}

export interface ISaleOrderPendingPayMessageScheduleCreate {
	dateToSend: string;
}

export enum FlightAncillaryTypeEnum {
	Seat = 'Seat',
	CabinBaggage = 'CabinBaggage',
	CheckedBaggage = 'CheckedBaggage',
	Pet = 'Pet',
	InFlightEntertainment = 'InFlightEntertainment',
	Meal = 'Meal',
	SpecialLuggage = 'SpecialLuggage',
	AccompaniedMinor = 'AccompaniedMinor',
}

export interface IFlightAncillaryTypeOption {
	id: FlightAncillaryTypeEnum;
	name: string;
}

export interface IFlightAncillaryService {
	keyId: string;
	reference: string;
	tariffProvider?: string | IProvider;
	unit: ServiceUnitEnum;
	cost?: string;
	markupPercentage: string;
	saleAmount?: string;
	saleAmountInLocalCurrency?: string;
	passengerIds: string[];
	passengersTickets: ITicket[];
	totalCost: string;
	totalSaleAmount: string;
	currency: string;
	serviceType: TypeServiceEnum;
	internalNotes?: string;
	gds?: GDSEnum[] | NotApplicable;
	vc: IValidatingCarrier[];
	limitDate: string;
	currencyRate?: number;
	totalSaleAmountInLocalCurrency?: number;
	flightAncillaryType: FlightAncillaryTypeEnum;
	issueRequest?: IIssueRequestDetail; //solo se envia en el detalle del ancillary no cuando se crea y es solo cuando se hace una solicitud de emision
	billingStatusDetail?: IServiceBillingStatusDetailDTO; //solo se envia en el detalle
	automaticProviderPaymentDateCalculation: boolean;
	providerManualPaymentDateLines: IServiceProviderManualPaymentDateLineDTO[];
	providerAutomaticPaymentDateLine?: IServiceProviderAutomaticPaymentDateInfoDTO; //solo se envia en el detalle
}

export interface ISaleOrderIdResponse {
	id: string;
}

export enum NetsuiteSyncStatusEnum {
	PendingSynchronization = 'PendingSynchronization',
	SynchronizationStart = 'SynchronizationStart',
	Synchronized = 'Synchronized',
	CreatingPurchaseOrders = 'CreatingPurchaseOrders',
	UpdatingPurchaseOrders = 'UpdatingPurchaseOrders',
	Error = 'Error',
}

export interface INetsuiteSyncStatusType {
	id: NetsuiteSyncStatusEnum;
	name: string;
}

export const netsuiteSyncStatusTypes: INetsuiteSyncStatusType[] = [
	{
		id: NetsuiteSyncStatusEnum.PendingSynchronization,
		name: 'Pendiente de sincronizar ⏳',
	},
	{
		id: NetsuiteSyncStatusEnum.SynchronizationStart,
		name: 'Sincronización iniciada 🚀',
	},
	{
		id: NetsuiteSyncStatusEnum.Synchronized,
		name: 'Sincronizado ✅',
	},
	{
		id: NetsuiteSyncStatusEnum.CreatingPurchaseOrders,
		name: 'Creando órdenes de compra 📝',
	},
	{
		id: NetsuiteSyncStatusEnum.UpdatingPurchaseOrders,
		name: 'Actualizando órdenes de compra 🔄',
	},
	{
		id: NetsuiteSyncStatusEnum.Error,
		name: 'Error ❌',
	},
];

export interface IInvoiceInfo {
	invoiceId: string;
	fileName: string;
	base64: string;
}

export interface IUserList {
	id: number;
	username: string;
	email: string;
	name: string;
	active: boolean;
}

export interface IServiceListItem {
	id: string;
	saleOrderId: string;
	saleOrderDealId: number;
	saleOrderIsBitrixDeal: boolean;
	agencyId: number;
	serviceType: TypeServiceEnum;
	reference: string;
	tariffProviderFantasyName: string;
	paymentDateToProvider: string | null; // LocalDate (ISO yyyy-mm-dd)
	totalCostMainCurrency: number;
	totalSaleAmountMainCurrency: number;
	voucherNotes: string | null;
	internalNotes: string | null;
	minStartDate: string | null; // LocalDate
	maxEndDate: string | null; // LocalDate
	enable: boolean;
	created: string; // LocalDateTime (ISO)
	createdBy: string;
	updated: string | null; // LocalDateTime (ISO)
	updatedBy: string | null;
}

export interface ISalesOrderServicesList {
	page: number;
	size: number;
	total: number;
	sort?: string;
	elements: IServiceListItem[];
}

// ==================== TIPOS PARA SOLICITUDES OPERATIVAS ====================

export enum OperationalRequestCategoryEnum {
	Operations = 'Operations',
	Emissions = 'Emissions',
	Refunds = 'Refunds',
	CreditCardInquiry = 'CreditCardInquiry',
}

export enum OperationsRequestTypeEnum {
	LateArrival = 'LateArrival',
	Modification = 'Modification',
	Cancellation = 'Cancellation',
}

export enum EmissionsRequestTypeEnum {
	VoidTicket = 'VoidTicket',
	OpenTicket = 'OpenTicket',
	SpecialServices = 'SpecialServices',
	InvoluntaryChangeConfirmation = 'InvoluntaryChangeConfirmation',
	VoluntaryChangeQuotation = 'VoluntaryChangeQuotation',
	NameCorrection = 'NameCorrection',
	AddOnsQuotation = 'AddOnsQuotation',
}

export enum RefundRequestTypeEnum {
	Voluntary = 'Voluntary',
	Immediate = 'Immediate',
}
export enum CreditCardInquiryTypeEnum {
	Chargeback = 'Chargeback',
	DuplicateCharge = 'DuplicateCharge',
	MissingCharge = 'MissingCharge',
	Reprocessing = 'Reprocessing',
}

export enum RefundScopeEnum {
	FullService = 'FullService',
	SpecificTickets = 'SpecificTickets',
}

export enum RefundReasonEnum {
	VoluntaryTaxesOnly = 'VoluntaryTaxesOnly',
	VoluntaryWithPenalty = 'VoluntaryWithPenalty',
	ReschedulingNotAccepted = 'ReschedulingNotAccepted',
	IllnessOrDeath = 'IllnessOrDeath',
}

export enum RefundBankAccountType {
	SavingsAccount = 'SavingsAccount',
	CheckingAccount = 'CheckingAccount',
}

export interface IRefundPaymentInfo {
	files?: IBase64Attachment[];
	refundAmountToCustomer: number;
	currency: string;
	bankAccount: IBankAccountInfo;
}

export enum TicketScopeEnum {
	AllTickets = 'AllTickets',
	SpecificTickets = 'SpecificTickets',
}

export interface IBankAccountInfo {
	bank: string;
	holder: string;
	accountType: RefundBankAccountType | string;
	currency: string;
	accountNumber: string;
}

export interface IRefundItem {
	serviceId: string;
	serviceType: TypeServiceEnum;
	scope: RefundScopeEnum;
	refundReason?: RefundReasonEnum | string;
	specificItems?: string[]; // eTickets
	additionalComments?: string;
}

export interface IBase64Attachment {
	fileName: string;
	contentType: string;
	base64: string;
	sizeBytes: number;
}

export interface IOperationalRequestBase {
	requestCategory: OperationalRequestCategoryEnum;
	saleOrderId: string;
	serviceIds: string[];
	observations?: string;
	attachments?: IBase64Attachment[];
}

export interface IOperationsServiceRequest extends IOperationalRequestBase {
	requestCategory: OperationalRequestCategoryEnum.Operations;
	requestType: OperationsRequestTypeEnum;
}

export interface IEmissionsRequestServiceDetail {
	serviceId: string;
	gdsFormat: string;
}

export enum EmissionRequestAddOnTypeEnum {
	Seat = 'Seat',
	CabinBaggage = 'CabinBaggage',
	HoldBaggage = 'HoldBaggage',
	Pet = 'Pet',
	Entertainment = 'Entertainment',
	Meals = 'Meals',
	SpecialBaggage = 'SpecialBaggage',
	UnaccompaniedMinor = 'UnaccompaniedMinor',
	General = 'General',
}

export interface IEmissionsRequestAddOnDetailSeat {
	seatNumber?: string;
	seatLocation?: string;
}

export interface IEmissionsRequestAddOnDetailHoldBaggage {
	holdBaggageQuantity?: string;
	holdBaggageWeight?: string;
}

export enum EmissionRequestPetLocationEnum {
	Cabin = 'Cabin', // Cabina
	Hold = 'Hold', // Bodega
}

export interface IEmissionsRequestAddOnDetailPet {
	petBreed?: string;
	petWeight?: string;
	petCarrierWeight?: string;
	petCarrierDimensions?: string;
	petLocation?: EmissionRequestPetLocationEnum;
}

export interface IEmissionsRequestAddOnDetailMeals {
	mealType?: string;
}

export interface IEmissionsRequestAddOnDetailSpecialBaggage {
	specialBaggageType?: string;
	specialBaggageDimensions?: string;
	specialBaggageWeight?: string;
}

export interface IEmissionsRequestAddOnDetailSpecialBaggage {
	specialBaggageType?: string;
	specialBaggageDimensions?: string;
	specialBaggageWeight?: string;
}

export interface IEmissionsRequestAddOnDetailEntertainment {
	entertainmentScope?: string;
	entertainmentPreference?: string;
}

export interface IEmissionsRequestAddOnDetailCabinBaggage {
	cabinBaggageQuantity?: string;
	cabinBaggageDimensions?: string;
}
export interface IEmissionsRequestAddOnDetailUnaccompaniedMinor {
	minorAge?: string;
	minorSegments?: string;
	minorTravelingAlone?: string;
}
export interface IEmissionsRequestAddOnDetailGeneral {
	generalInfo?: string;
}

export type EmissionsRequestAddOnDetail =
	| IEmissionsRequestAddOnDetailSeat
	| IEmissionsRequestAddOnDetailCabinBaggage
	| IEmissionsRequestAddOnDetailHoldBaggage
	| IEmissionsRequestAddOnDetailPet
	| IEmissionsRequestAddOnDetailEntertainment
	| IEmissionsRequestAddOnDetailMeals
	| IEmissionsRequestAddOnDetailSpecialBaggage
	| IEmissionsRequestAddOnDetailUnaccompaniedMinor
	| IEmissionsRequestAddOnDetailGeneral;

export enum EmissionRequestSpecialServiceTypeEnum {
	WheelchairAirport = 'WheelchairAirport',
	UnaccompaniedMinor = 'UnaccompaniedMinor',
	SpecialMeal = 'SpecialMeal',
	OxygenOnboard = 'OxygenOnboard',
	PriorityBoarding = 'PriorityBoarding',
	DisabilityAssistance = 'DisabilityAssistance',
}

export interface IEmissionRequestTicketDetailItem {
	ticket: string;
	specialService?: EmissionRequestSpecialServiceTypeEnum;
	currentName?: string;
	correctName?: string;
	addOnType?: EmissionRequestAddOnTypeEnum;
	addOnDetails?: EmissionsRequestAddOnDetail;
}

export interface IEmissionsRequestTicketDetailContainer {
	serviceId: string;
	tickets: IEmissionRequestTicketDetailItem[];
}

export interface IEmissionsRequest extends IOperationalRequestBase {
	requestCategory: OperationalRequestCategoryEnum.Emissions;
	requestType: EmissionsRequestTypeEnum;
	ticketScope: TicketScopeEnum;
	specificTickets?: string[];
	serviceDetails?: IEmissionsRequestServiceDetail[];
	ticketsDetailByService?: IEmissionsRequestTicketDetailContainer[];
}

export interface IRefundRequest extends IOperationalRequestBase {
	requestCategory: OperationalRequestCategoryEnum.Refunds;
	requestType: RefundRequestTypeEnum;
	refundItems: IRefundItem[];
	paymentInfo?: IRefundPaymentInfo;
	additionalComments?: string;
	supportingDocuments?: IBase64Attachment[];
}
export interface ICreditCardInquiryTransactionInfo {
	transactionId: string;
	authorizationId?: number;
	relationType: PaymentReceivedRelationTypeEnum;
	platformType: PaymentReceivedPlatformTypeEnum;
}
export interface ICreditCardInquiryRequest extends IOperationalRequestBase {
	requestCategory: OperationalRequestCategoryEnum.CreditCardInquiry;
	requestType: CreditCardInquiryTypeEnum;
	transactionsInfo: ICreditCardInquiryTransactionInfo[];
	description: string;
}

export type IOperationalRequest =
	| IOperationsServiceRequest
	| IEmissionsRequest
	| IRefundRequest
	| ICreditCardInquiryRequest;

// ─── Módulo: Pagos a Proveedores ─────────────────────────────────────────────

export enum ProviderPayableLineTypeEnum {
	Service = 'Service',
	FlightTicket = 'FlightTicket',
	FlightAncillaryTicket = 'FlightAncillaryTicket',
}

export enum ProviderPayableLinePaymentStatusEnum {
	Pending = 'Pending',
	Partial = 'Partial',
	Paid = 'Paid',
	Cancelled = 'Cancelled',
}

export enum ProviderPayableLinePaymentTypeEnum {
	CorporateCard = 'CorporateCard',
	C2A = 'C2A',
	BankTransfer = 'BankTransfer',
	Check = 'Check',
	Cash = 'Cash',
	Other = 'Other',
}

export interface IProviderPayableLine {
	id: string;
	serviceId: string;
	saleOrderId: string;
	providerId: string;
	providerName?: string;
	payableType: ProviderPayableLineTypeEnum;
	passengerId?: string;
	passengerName?: string;
	ticket?: string;
	currency: string;
	payableAmount: number;
	totalPaid: number;
	paymentStatus: ProviderPayableLinePaymentStatusEnum;
	/** Mercado/subsidiaria de la sale order — código ISO país (ej: "UY", "CO") */
	sellerMarket?: string;
	createdBy: string;
	created: string;
	updatedBy?: string;
	updated?: string;
}

export interface IProviderPayableLinePayment {
	id: string;
	providerPayableLineId: string;
	paymentDate: string;
	/** Siempre en USD — es el monto contable de la orden */
	amount: number;
	paymentType: ProviderPayableLinePaymentTypeEnum;
	notes?: string;
	observation?: string;
	/**
	 * Moneda real con la que se efectuó el pago desde la cuenta bancaria.
	 * Informativo para conciliación bancaria. Default: "USD".
	 */
	localCurrency?: string;
	/**
	 * Tipo de cambio aplicado al momento del pago.
	 * Informativo para conciliación bancaria. Default: 1.
	 * Ejemplo: localCurrency "UYU", exchangeRate 42 → salieron $16.800 UYU de la cuenta.
	 */
	exchangeRate?: number;
	createdBy: string;
	created: string;
	updatedBy?: string;
	updated?: string;
}

export interface IProviderPayableLinePaymentCreateDTO {
	paymentDate: string;
	amount: number;
	paymentType: ProviderPayableLinePaymentTypeEnum;
	notes?: string;
	observation?: string;
	/** Default: "USD" */
	localCurrency?: string;
	/** Default: 1 */
	exchangeRate?: number;
}

export interface IProviderPayableLinePaymentUpdateDTO {
	paymentDate: string;
	amount: number;
	paymentType: ProviderPayableLinePaymentTypeEnum;
	notes?: string;
	observation?: string;
	localCurrency?: string;
	exchangeRate?: number;
}

export interface IBulkPaymentItem {
	payableLineId: string;
	payments: IProviderPayableLinePaymentCreateDTO[];
}

export interface IBulkPaymentResultItem {
	paymentId?: string;
	status: 'Created' | 'Failed';
	error?: string;
}

export interface IBulkPaymentResponseItem {
	payableLineId: string;
	results: IBulkPaymentResultItem[];
}

export interface IOperationalRequestListItem {
	id: string;
	requestCategory: OperationalRequestCategoryEnum;
	requestType: string;
	caseId: number;
	createdAt: string;
	createdBy: string;
}

export interface IOperationalRequestResponse {
	id: string;
	requestCategory: OperationalRequestCategoryEnum;
	requestType: string;
	caseId: number;
	createdAt: string;
	createdBy: string;
}

// ── Provider Payables List ────────────────────────────────────────────────────

export interface IProviderPayableLineListItem {
	id: string;
	saleOrderId: string;
	/** ID del deal/caso en el CRM */
	dealId: number;
	isBitrixDeal: boolean;
	serviceId: string;
	providerId: string;
	providerName: string;
	payableType: ProviderPayableLineTypeEnum;
	passengerId?: string;
	passengerName?: string;
	/** Número de ticket aéreo o localizador de reserva según el tipo */
	ticket?: string;
	currency: string;
	payableAmount: number;
	totalPaid: number;
	pending: number;
	paymentStatus: ProviderPayableLinePaymentStatusEnum;
	/** Mercado/subsidiaria — código ISO país (ej: "UY", "CO") */
	sellerMarket: string;
	created: string;
	createdBy: string;
	// ── Campos de conciliación (calculados por el backend) ──────────────────
	/** La sale order tiene al menos 1 cobro registrado */
	hasAtLeastOneClientPayment?: boolean;
	/** La sale order tiene saldo de cliente pendiente */
	hasClientPendingBalance?: boolean;
	/** El cobro del cliente cubre o supera la sumatoria de costos de tipo Flight */
	clientPaymentCoversFlightCosts?: boolean;
}

export interface IProviderPayableLineListResponse {
	page: number;
	size: number;
	total: number;
	elements: IProviderPayableLineListItem[];
}

export interface IProviderPayableLineListFilters {
	/** IDs de proveedores seleccionados (multiselect) */
	providerIds?: string[];
	ticket?: string;
	/** ID de orden de venta */
	saleOrderId?: string;
	/** Mercado del vendedor (ISO país: UY, CO, PY, BO) */
	sellerMarket?: string;
	/** Tipos de línea a pagar (multiselect) */
	payableTypes?: ProviderPayableLineTypeEnum[];
	/** Tipos de pago seleccionados — lógica: mostrar línea si ALGUNO de sus pagos tiene ese tipo */
	paymentTypes?: ProviderPayableLinePaymentTypeEnum[];
	/** Estados seleccionados (multiselect) */
	paymentStatuses?: ProviderPayableLinePaymentStatusEnum[];
	/** Fecha real de pago — desde */
	dateFrom?: string;
	/** Fecha real de pago — hasta */
	dateTo?: string;
	/** Fecha de pago programado (schedule) — desde */
	scheduleDateFrom?: string;
	/** Fecha de pago programado (schedule) — hasta */
	scheduleDateTo?: string;
}

/** Filtros para el endpoint /provider-payable-lines/schedule (scheduleDateFrom y scheduleDateTo son obligatorios) */
export interface IProviderPayableLineScheduleFilters {
	providerIds?: string[];
	ticket?: string;
	saleOrderId?: string;
	sellerMarket?: string;
	payableTypes?: ProviderPayableLineTypeEnum[];
	paymentTypes?: ProviderPayableLinePaymentTypeEnum[];
	paymentStatuses?: ProviderPayableLinePaymentStatusEnum[];
	dateFrom?: string;
	dateTo?: string;
	scheduleDateFrom: string;
	scheduleDateTo: string;
}

export interface IProviderPayableLineScheduleItem {
	id: string;
	saleOrderId: string;
	dealId: number;
	isBitrixDeal: boolean;
	serviceId: string;
	serviceType: string;
	providerId: string;
	providerName: string;
	payableType: ProviderPayableLineTypeEnum;
	passengerId?: string;
	passengerName?: string;
	ticket?: string;
	currency: string;
	payableAmount: number;
	totalPaid: number;
	pending: number;
	paymentStatus: ProviderPayableLinePaymentStatusEnum;
	sellerMarket: string;
	created: string;
	createdBy: string;
	hasAtLeastOneClientPayment?: boolean;
	hasClientPendingBalance?: boolean;
	clientPaymentCoversFlightCosts?: boolean;
	paymentLineIndex: number;
	scheduleAmount: number;
}

export interface IProviderPayableLineScheduleGroup {
	schedulePaymentDate: string;
	totalAmount: number;
	lines: IProviderPayableLineScheduleItem[];
}

export interface IProviderPayableLineScheduleResponse {
	paymentSchedule: IProviderPayableLineScheduleGroup[];
}

/**
 * Respuesta del endpoint GET /provider-payable-lines/pending
 * Devuelve solo los IDs encontrados con su monto pendiente actual.
 * Usado por el bulk importer para validar antes de insertar.
 */
export interface IProviderPayableLinePending {
	id: string;
	/** Monto pendiente de pago en USD */
	pending: number;
}
/**
 * Ítem del listado global de pagos a proveedores.
 * Cada registro representa un IProviderPayableLinePayment con contexto
 * de su línea padre (proveedor, ticket, orden, deal, mercado).
 * Endpoint: GET /provider-payable-line-payments (paginado con filtros)
 */
export interface IProviderPayableLinePaymentListItem {
	// — Identificadores —
	id: string;
	providerPayableLineId: string;
	saleOrderId: string;
	dealId: number;
	isBitrixDeal: boolean;
	// — Contexto de la línea padre —
	providerId: string;
	providerName: string;
	payableType: ProviderPayableLineTypeEnum;
	passengerId?: string;
	passengerName?: string;
	ticket?: string;
	/** Mercado del vendedor (ISO país: UY, CO…). Determina la subsidiaria contable. */
	sellerMarket: string;
	// — Datos del pago —
	paymentDate: string;
	/** Monto siempre en USD */
	amount: number;
	paymentType: ProviderPayableLinePaymentTypeEnum;
	notes?: string;
	observation?: string;
	/**
	 * Moneda real de la cuenta bancaria utilizada (informativo, para conciliación).
	 * El campo `amount` siempre está en USD.
	 */
	localCurrency?: string;
	/** Tipo de cambio aplicado al momento del pago (informativo). Default: 1 */
	exchangeRate?: number;
	createdBy: string;
	created: string;
}

export interface IProviderPayableLinePaymentListResponse {
	page: number;
	size: number;
	total: number;
	sort?: string;
	totalAmount?: number;
	elements: IProviderPayableLinePaymentListItem[];
}

export interface IProviderPayableLinePaymentListFilters {
	providerIds?: string[];
	ticket?: string;
	paymentTypes?: ProviderPayableLinePaymentTypeEnum[];
	/** Filtro por mercado del vendedor (ISO país: UY, CO…) */
	sellerMarkets?: string[];
	/** Rango sobre paymentDate (YYYY-MM-DD) */
	dateFrom?: string;
	dateTo?: string;
	/** Rango sobre created (YYYY-MM-DD) */
	createdFrom?: string;
	createdTo?: string;
	notes?: string;
	observation?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// FEATURE 2 — Cobros a Clientes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cobro recibido de un cliente.
 * Espejo del DTO PaymentNotificationReceived del backend.
 * Endpoint: GET /saleorders/{id}/payment-received
 */
/**
 * Entidad central de cobros a clientes.
 * Generada automáticamente por cada paymentNotificationReceived.
 * Endpoint: GET /receipts · PATCH /receipts/{id}/issue · PATCH /receipts/{id}/reconcile
 */
export interface IReceipt {
	// — Identidad del receipt —
	id: string;
	/** Número legible, formato "YYYY-NNNNN", asignado al crear. Ej: "2026-00042" */
	receiptNumber: string;
	issued: boolean;
	issuedAt?: string;
	issuedBy?: string;
	/** URL del PDF una vez emitido */
	pdfUrl?: string;
	/** Pasajero a nombre de quien se emitió el recibo */
	passengerId?: string;

	// — De paymentNotificationReceived —
	paymentNotificationId?: string;
	paymentRefundNotificationId?: string;
	saleOrderId?: string;
	dealId: number;
	isBitrixDeal: boolean;
	transactionId: string;
	/** Código de autorización — string */
	authorizationId?: string;
	transactionAmount: number;
	transactionCurrency: string;
	/** Monto siempre en USD */
	amountInMainCurrency: number;
	transactionAmountInLocalCurrency?: number;
	rateToConvertToLocalCurrency?: number;
	relationType: PaymentReceivedRelationTypeEnum;
	platformType: PaymentReceivedPlatformTypeEnum;
	/** BU: agencyId numérico */
	agencyId: number;
	createdAt: string;
	observations?: string;
	// — Campos extendidos de trazabilidad —
	extBankName?: string;
	extBankAccount?: string;
	extPaymentReference?: string;
	extAuthCode?: string;
	extCardStamp?: string;
	extCardLast4?: string;
	extBranchName?: string;
	extCashSourceType?: string;
	extGatewayTransactionId?: string;
	/** Código IATA de la aerolinea (para cobros C2A / MVOrderTransaction) */
	extAirlineName?: string;

	// — De saleorder (JOIN) —
	mainPassengerName?: string;
	segmentType?: SegmentEnum;

	// — Conciliación —
	reconciled: boolean;
	reconciledAt?: string;
	reconciledBy?: string;
}

export interface IReceiptListResponse {
	page: number;
	size: number;
	total: number;
	elements: IReceipt[];
}

export interface IReceiptFilters {
	// — Identidad —
	saleOrderId?: string;
	receiptNumber?: string;
	dealId?: number;
	reference?: string;
	passengerName?: string;

	// — Clasificación —
	agencyIds?: number[];
	segmentTypes?: SegmentEnum[];
	relationTypes?: PaymentReceivedRelationTypeEnum[];
	platformTypes?: PaymentReceivedPlatformTypeEnum[];
	currencies?: string[];

	// — Trazabilidad ext_ —
	bankName?: string;
	/** Marcas de tarjeta: 'VISA' | 'MASTERCARD' | 'AMEX' | … */
	cardStamps?: string[];

	// — Montos (en USD) —
	amountFrom?: number;
	amountTo?: number;

	// — Fechas creación —
	dateFrom?: string;
	dateTo?: string;

	// — Fechas conciliación —
	reconciledFrom?: string;
	reconciledTo?: string;

	// — Estado —
	/** true = solo conciliados, false = solo sin conciliar, undefined = todos */
	reconciled?: boolean;
	/** true = solo emitidos, false = solo sin emitir, undefined = todos */
	issued?: boolean;

	/** Búsqueda parcial (contains) sobre observations */
	observations?: string;
}
